import styled from '@emotion/styled'
import { AnimatedTitle } from 'app/components/Common/Animation/AnimatedTitle'
import { FadeInUp } from 'app/components/Common/Animation/FadeInUp'
import { Button } from 'app/components/Common/Button'
import { Props as ImageProps } from 'app/components/Common/Image'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { styles } from 'app/theme'
import { Media } from 'app/theme/media'
import { useVocabularyData } from 'app/utils/vocabulary'
import React, { memo } from 'react'

import { Info } from './Info'
import { Preview } from './Preview'

export interface Props {
  bookingButtonURL?: string
  description?: string
  images?: ImageProps[]
  index?: number
  languageCode: string
  pax?: string
  quotationPageURL?: string
  size?: string
  title?: string
}

export const Room = memo(function Room({
  bookingButtonURL,
  description,
  index,
  images,
  languageCode,
  pax,
  quotationPageURL,
  size,
  title,
}: Props) {
  if (!title) {
    return null
  }

  return (
    <Container id={title}>
      {images ? (
        <Media greaterThanOrEqual="desktopSmall">
          {(className: any, renderChildren: any) => {
            return renderChildren ? (
              <Preview
                className={className}
                images={images}
                languageCode={languageCode}
                title={title}
              />
            ) : null
          }}
        </Media>
      ) : null}

      <Wrapper>
        <FadeInUp>
          <Label>{`${(index || 0).toString().padStart(2, '0')} ${useVocabularyData('room', languageCode)}`}</Label>
        </FadeInUp>

        <Head dial={4} row space="between" wrap>
          {title ? <Title delayChildren={0.4} tag="h2" text={title} /> : null}

          {images ? (
            <Media lessThan="desktopSmall">
              {(className: any, renderChildren: any) => {
                return renderChildren ? (
                  <Preview
                    className={className}
                    images={images}
                    languageCode={languageCode}
                    title={title}
                  />
                ) : null
              }}
            </Media>
          ) : null}

          <Info pax={pax} size={size} />
        </Head>

        {description ? (
          <FadeInUp>
            <Description dangerouslySetInnerHTML={{ __html: description }} />
          </FadeInUp>
        ) : null}

        {bookingButtonURL || quotationPageURL ? (
          <Buttons>
            {bookingButtonURL ? (
              <CTA
                label={useVocabularyData('book', languageCode)}
                URL={bookingButtonURL}
              />
            ) : null}

            {quotationPageURL ? (
              <CTA
                label={useVocabularyData('ask', languageCode)}
                URL={quotationPageURL}
                variant="flat"
              />
            ) : null}
          </Buttons>
        ) : null}
      </Wrapper>
    </Container>
  )
})

const Container = styled.div`
  padding: 7.5rem 0;
  &:first-of-type {
    padding-top: 0;
  }
  &:nth-of-type(even) {
    background: ${({ theme }) => theme.colors.variants.neutralLight3};
    padding: 4.5rem 0;
  }

  @media (max-width: 1199px) {
    padding: 3rem 0 3.75rem;
    &:first-of-type {
      padding-top: 3rem;
    }
    &:nth-of-type(even) {
      padding: 3rem 0 3.75rem;
    }
  }
`

const Wrapper = styled.div`
  max-width: 66.667vw;
  margin-top: 3rem;
  padding: 0 5vw;

  @media (max-width: 1199px) {
    max-width: none;
    margin-top: 0;
    overflow: hidden;
    padding: 0;
  }
`

const Label = styled.div`
  ${styles.description}

  font-size: 0.875rem;
  line-height: 1.25rem;
  margin-bottom: 0.5625rem;

  @media (max-width: 1199px) {
    margin-bottom: 0.375rem;
    padding: 0 2rem;
  }
`

const Head = styled(FlexBox)``

const Title = styled(AnimatedTitle)`
  ${styles.title.medium}

  @media (max-width: 1199px) {
    padding: 0 2rem;
  }
`

const Description = styled.div`
  ${styles.description}

  margin-top: 2rem;

  @media (max-width: 1199px) {
    margin-top: 1.5rem;
    padding: 0 2rem;
  }
`

const Buttons = styled(FadeInUp)`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-top: 2rem;

  @media (max-width: 1199px) {
    padding: 0 2rem;
  }
`

const CTA = styled(Button)`
  &:nth-of-type(2) {
    margin-left: 2rem;
  }
`
